import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import usePushNotifications from '@/hooks/usePushNotifications';
import { toast } from 'sonner';
import axios from 'axios';

import { Menu, X, HelpCircle, LogOut, History, Shield, Download, ChevronDown, ArrowLeft, Heart, Bell, BellRing, Compass, User, CalendarDays, Calendar, Ticket, Fuel, Users, MessageSquare, Zap, ShoppingBag, AlertTriangle, Settings, SlidersHorizontal, Globe, Package } from 'lucide-react';
import SmartInstallModal from '@/components/SmartInstallModal';
import NotificationBell from '@/components/NotificationBell';
import MobileTabBar from '@/components/layout/MobileTabBar';
import MobileMenuOverlay from '@/components/layout/MobileMenuOverlay';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const NAV_LINKS = [
  { path: '/events', label: 'Events' },
  { path: '/rollflix', label: 'Community' },
  { path: '/safe-space', label: 'Safe Space' },
  { path: '/sk8-shop', label: 'Community Listings' },
  { path: '/calendar', label: 'Calendar' },
];

const DROPDOWN_LINKS = [
  { path: '/hire', label: 'Hire Hub' },
  { path: '/ranting-spot', label: 'Unfiltered' },
  { path: '/floor-tax', label: 'Floor Tax' },
  { path: '/fuel-estimator', label: 'Fuel Calculator' },
  { path: '/safety', label: 'Safety' },
];

const Navbar = ({ activePage: activeProp, noMobileTabBar = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { mode } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, subscribe: pushSubscribe } = usePushNotifications();
  const [pushLoading, setPushLoading] = useState(false);
  const [listingInquiries, setListingInquiries] = useState({ sk8shop: 0, hire: 0, total: 0 });

  // Poll unread listing inquiries every 30s (only when logged in)
  useEffect(() => {
    if (!user) { setListingInquiries({ sk8shop: 0, hire: 0, total: 0 }); return; }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/unread-listing-count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setListingInquiries(data);
      } catch (_) { /* ignore */ }
    };
    fetchCount();
    const int = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(int); };
  }, [user]);
  
  const handlePushSubscribe = async () => {
    toast.info('Setting up notifications...');
    setPushLoading(true);
    try {
      const result = await pushSubscribe();
      if (result) {
        toast.success('Notifications enabled!');
      } else {
        toast.error('Could not enable notifications. Check your browser allows notifications for this site.');
      }
    } catch (err) {
      console.error('Push handler error:', err);
      toast.error('Notification setup failed: ' + (err.message || 'Unknown error'));
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestLocalNotification = async () => {
    try {
      if (Notification.permission !== 'granted') {
        toast.error('Notification permission not granted');
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration('/');
      if (!reg) {
        toast.error('No service worker registered');
        return;
      }
      await reg.showNotification('RollM8s Test', {
        body: 'Local notification test - this proves your browser can show notifications!',
        icon: '/logo192.png',
        badge: '/logo192.png',
        vibrate: [200, 100, 200],
      });
      toast.success('Notification sent locally - check your notification tray!');
    } catch (err) {
      toast.error('Local notification failed: ' + err.message);
    }
  };
  const [navHeight, setNavHeight] = useState(0);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const desktopMenuRef = useRef(null);
  const navRef = useRef(null);
  
  const activePage = activeProp || location.pathname;
  const isHomePage = location.pathname === '/';
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

  const [crisisZoneVisible, setCrisisZoneVisible] = useState(false);

  // Fetch crisis zone visibility
  useEffect(() => {
    axios.get(`${API}/admin/crisis-zone-visibility`)
      .then(res => setCrisisZoneVisible(res.data.crisis_zone_visible))
      .catch(() => {});
  }, []);

  // Capture install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Close desktop dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (desktopMenuRef.current && !desktopMenuRef.current.contains(e.target)) {
        setDesktopMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInstall = () => {
    setShowInstallModal(true);
  };

  // Handle swipe to close menu
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    const swipeDistance = touchStartY.current - touchEndY.current;
    // If swiped up more than 50px, close the menu
    if (swipeDistance > 50) {
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
    navigate('/');
  };

  const handleReplayTour = () => {
    setDesktopMenuOpen(false);
    setMobileMenuOpen(false);
    localStorage.setItem('rollm8s_replay_tour', 'true');
    navigate('/dashboard');
  };

  useEffect(() => {
    if (!navRef.current) return;
    // Measure the FULL rendered height including padding + border, not just content.
    // contentRect.height excludes padding, which was making the spacer short by
    // ~12-20px and letting page headers slide under the nav.
    const measure = () => {
      if (navRef.current) {
        const h = navRef.current.getBoundingClientRect().height;
        setNavHeight(h);
        // Expose to descendants (like MoreMenuSheet) that need to anchor
        // themselves directly below the fixed navbar.
        try {
          document.documentElement.style.setProperty('--navbar-height', `${h}px`);
        } catch { /* ignore SSR */ }
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(navRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <>
    <nav ref={navRef} data-mobile-nav="true" className={`fixed top-0 left-0 right-0 z-50 border-b border-border/50 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] md:pt-0 ${activePage === '/safe-space' ? 'bg-black' : 'bg-background/95 backdrop-blur-md'}`}>
      <div className="max-w-7xl mx-auto px-3 md:px-8">
        <div className="flex items-center justify-between py-2">
          {/* Back button + Logo */}
          <div className="flex items-center shrink-0 min-w-0">
            <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-1">
            <img 
              src={mode === 'dark' ? '/rollm8s-logo-dark.png' : '/rollm8s-logo-light.png'}
              alt="RollM8s Logo" 
              className="w-[4rem] h-[4rem] md:w-[10rem] md:h-[10rem] object-contain flex-shrink-0"
            />
            <div className="flex flex-col">
              <span className="font-logo text-[2rem] md:text-[4.5rem] tracking-wide leading-tight">
                <span className="text-foreground">ROLL</span>
                <span className="gradient-text">M8S</span>
              </span>
              {activePage === '/' && (
                <span className="text-[8px] md:text-[10px] font-bold uppercase leading-none block w-0 min-w-full" style={{ textAlign: 'justify', textAlignLast: 'justify' }}>
                  <span className="text-foreground">THE UK'S </span>
                  <span className="text-primary">ROLLER SKATE </span>
                  <span className="text-secondary">COMMUNITY & CO</span>
                </span>
              )}
            </div>
          </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4 text-sm">
            {NAV_LINKS.map((link) => {
              const badgeCount = link.path === '/sk8-shop' ? listingInquiries.sk8shop : 0;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`navbar-link-${link.path}`}
                  className={`relative ${
                    activePage === link.path
                      ? 'text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground transition-colors'
                  }`}
                >
                  {link.label}
                  {badgeCount > 0 && (
                    <span className="absolute -top-1.5 -right-3 bg-pink-500 text-white text-[9px] font-bold rounded-full min-w-[15px] h-[15px] px-1 flex items-center justify-center" data-testid="navbar-sk8shop-badge">
                      {badgeCount > 9 ? '9+' : badgeCount}
                    </span>
                  )}
                </Link>
              );
            })}
            {crisisZoneVisible && (
              <Link
                to="/blackout"
                data-testid="navbar-link-crisis-zone"
                className={
                  activePage === '/blackout'
                    ? 'text-primary font-medium'
                    : 'text-muted-foreground hover:text-foreground transition-colors'
                }
              >
                Blackout Zone
              </Link>
            )}
            {user ? (
              <>
                {pushSupported && !pushSubscribed && (
                  <span className="hidden" />
                )}
                {pushSubscribed && (
                  <span className="hidden" />
                )}
                <NotificationBell />
                <Link
                  to="/dashboard"
                  className={
                    activePage === '/dashboard'
                      ? 'text-primary font-bold'
                      : 'text-primary font-medium hover:text-primary/80 transition-colors'
                  }
                >
                  Dashboard
                </Link>
                {/* Dropdown Menu */}
                <div className="relative" ref={desktopMenuRef}>
                  <button
                    onClick={() => setDesktopMenuOpen(!desktopMenuOpen)}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="navbar-desktop-dropdown-btn"
                  >
                    <Menu className="w-4 h-4" />
                    Menu
                    <ChevronDown className={`w-3 h-3 transition-transform ${desktopMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {desktopMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 rounded-xl glass border border-border/50 shadow-xl py-2 z-50 animate-fade-in">
                      <Link to="/my-profile" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                        Profile Settings
                      </Link>
                      {DROPDOWN_LINKS.map((link) => {
                        const hireBadge = link.path === '/hire' ? listingInquiries.hire : 0;
                        return (
                          <Link key={link.path} to={link.path} className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors flex items-center justify-between" onClick={() => setDesktopMenuOpen(false)}>
                            <span>{link.label}</span>
                            {hireBadge > 0 && (
                              <span className="bg-pink-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="dropdown-hire-badge">
                                {hireBadge > 9 ? '9+' : hireBadge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                      <Link to="/support" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                        Support
                      </Link>
                      <Link to="/messages" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                        Messages
                      </Link>
                      <Link to="/trip-history" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                        Trip History
                      </Link>
                      <Link to="/my-tickets" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                        Ticket Wallet
                      </Link>
                      <Link to="/world-trips" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                        World Trips
                      </Link>
                      <Link to="/faq" className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors" onClick={() => setDesktopMenuOpen(false)}>
                        FAQ
                      </Link>
                      {!isStandalone && (
                        <button onClick={() => { handleInstall(); setDesktopMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-primary hover:bg-primary/10 transition-colors flex items-center gap-2" data-testid="desktop-install-btn">
                          <Download className="w-4 h-4" />
                          Download App
                        </button>
                      )}
                      {(user.is_admin || user.role === 'admin') && (
                        <>
                          <div className="border-t border-border/30 my-1" />
                          <Link to="/admin" className="block px-4 py-2 text-sm text-yellow-500 hover:bg-yellow-500/10 transition-colors font-medium" onClick={() => setDesktopMenuOpen(false)}>
                            Admin Panel
                          </Link>
                          <Link to="/site-settings" className="block px-4 py-2 text-sm text-yellow-500 hover:bg-yellow-500/10 transition-colors font-medium" onClick={() => setDesktopMenuOpen(false)}>
                            Site Settings
                          </Link>
                        </>
                      )}
                      <div className="border-t border-border/30 my-1" />
                      {/* Tour hidden */}
                      <button
                        onClick={() => { handleLogout(); setDesktopMenuOpen(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Guest Dropdown */}
                <div className="relative" ref={desktopMenuRef}>
                  <button
                    onClick={() => setDesktopMenuOpen(!desktopMenuOpen)}
                    className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="navbar-desktop-dropdown-btn-guest"
                  >
                    <Menu className="w-4 h-4" />
                    More
                    <ChevronDown className={`w-3 h-3 transition-transform ${desktopMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {desktopMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-48 rounded-xl glass border border-border/50 shadow-xl py-2 z-50 animate-fade-in">
                      {DROPDOWN_LINKS.map((link) => {
                        const hireBadge = link.path === '/hire' ? listingInquiries.hire : 0;
                        return (
                          <Link key={link.path} to={link.path} className="block px-4 py-2 text-sm text-foreground hover:bg-primary/10 transition-colors flex items-center justify-between" onClick={() => setDesktopMenuOpen(false)}>
                            <span>{link.label}</span>
                            {hireBadge > 0 && (
                              <span className="bg-pink-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center" data-testid="dropdown-hire-badge">
                                {hireBadge > 9 ? '9+' : hireBadge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                      {!isStandalone && (
                        <button onClick={() => { handleInstall(); setDesktopMenuOpen(false); }} className="w-full text-left px-4 py-2 text-sm text-primary hover:bg-primary/10 transition-colors flex items-center gap-2" data-testid="desktop-install-btn-guest">
                          <Download className="w-4 h-4" />
                          Download App
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <Link
                  to="/login"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  Login
                </Link>
                <Button
                  onClick={() => navigate('/register')}
                  className="rounded-full bg-primary hover:bg-primary/90"
                >
                  Register <span className="inline-block animate-bounce-x ml-1">&rarr;</span>
                </Button>
              </>
            )}
          </div>

          {/* Mobile chrome — icon-only buttons for a native app feel.
              We keep the in-app Back button visible because iOS users have
              no hardware back; on Android the hook in App.js also wires
              the system back gesture to the same navigate(-1) action. */}
          <div className="md:hidden flex items-center gap-1 shrink-0">
            {user && <NotificationBell />}
            {!isHomePage && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Back"
                title="Back"
                className="w-10 h-10 inline-flex items-center justify-center rounded-full text-foreground/80 hover:text-foreground hover:bg-foreground/10 active:bg-foreground/15 transition"
                data-testid="mobile-back-btn"
              >
                <ArrowLeft className="w-5 h-5" strokeWidth={2.25} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              title="Menu"
              className="w-12 h-12 inline-flex items-center justify-center rounded-full text-white shadow-lg shadow-primary/30 bg-gradient-to-br from-primary to-secondary hover:brightness-110 active:scale-95 transition"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-7 h-7" strokeWidth={2.5} /> : <Menu className="w-7 h-7" strokeWidth={2.5} />}
            </button>
          </div>
        </div>
      </div>
    </nav>
    {/* Mobile Menu Dropdown — rendered OUTSIDE <nav> because the nav's
        backdrop-blur-md creates a containing block that would clip the
        menu's fixed-position overlay to the nav's size. */}
    <MobileMenuOverlay
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      user={user}
      crisisZoneVisible={crisisZoneVisible}
      isStandalone={isStandalone}
      onInstall={handleInstall}
      onLogout={handleLogout}
    />
    {/* Spacer to push content below fixed navbar */}
    {/* Spacer matches the nav's actual rendered height (border-box) + 8px breathing
        room. We Math.max against a 88px floor because: (a) on first paint the
        ResizeObserver hasn't measured yet (navHeight is 0); (b) on mobile during
        the very first render the safe-area-inset CSS hasn't fully applied so
        even the measured value can read short by ~15-20px. The floor guarantees
        the chat header & Sk8 Shop product card don't slide under the fixed nav
        on pages that lock scrolling (e.g. ChatPage's h-[100dvh] layout). */}
    <div style={{ height: Math.max(navHeight || 0, 88) + 8 }} aria-hidden="true" />

    {/* Mobile bottom tab bar — shown on small screens + native app.
        DashboardPage sets noMobileTabBar so we don't double-mount when
        it wraps Navbar in a `hidden md:block` shell and renders its own
        MobileTabBar directly. */}
    {!noMobileTabBar && <MobileTabBar onMorePress={() => setMobileMenuOpen(v => !v)} />}

    <SmartInstallModal
      open={showInstallModal}
      onClose={() => setShowInstallModal(false)}
      deferredPrompt={installPrompt}
      onPromptUsed={() => setInstallPrompt(null)}
    />
    </>
  );
};

export default Navbar;
